import React, { FunctionComponent, useEffect, useState } from 'react';
import { Paper, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, IconButton } from '@material-ui/core';
import styles from './AdjustersReferralList.Styles';
import MaterialTable from '../../../generic-components/material-table/MaterialTable.Component';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { MaterialTableProps } from 'material-table';
import { ReferralStatusTypesEnum } from '../../../../support/enums/ReferralStatusTypesEnum';
import ReferralStatusEntity from '../../../../entities/ReferralStatus.Entity';
import ReferralEntity from '../../../../entities/Referral.Entity';
import DeleteIcon from '@material-ui/icons/Delete';
import CancelIcon from '@material-ui/icons/Cancel';
import CloseIcon from '@material-ui/icons/Close';
import { Confirm } from '../../../generic-components/confirm/Confirm.Component';
import { useDateFilter } from '../../../../support/custom-hooks/useDateFilter';

interface IAdjustersReferralListProps {
    referrals: Array<IReferralEntityTable>;
    referralStatus: Array<ReferralStatusEntity>;
    handleNewReferralClick: any;
    handleDeleteReferral: (referralId: number) => void;
    handleCancelReferral: (referralId: number, reasonForCancelation: string) => void;
}
export interface IReferralEntityTable extends ReferralEntity {
    joinedServiceTypeNames: string;
}

const AdjustersReferralListTable: FunctionComponent<{ tableProps: MaterialTableProps<ReferralEntity> }> = ({ tableProps }) => {
    return (
        <Paper className={styles().clientListPaper}>
            <MaterialTable tableProps={tableProps} />
        </Paper>
    );
};

const CancelIconComponent: FunctionComponent<{ rowData: any }> = ({ rowData }) => {
    return rowData.disabled ? null : <CancelIcon style={{ fill: 'rgba(0,0,0,0.54)' }} />;
};

export const AdjustersReferralList: FunctionComponent<IAdjustersReferralListProps> = ({
    handleDeleteReferral,
    handleCancelReferral,
    referralStatus,
    referrals,
    handleNewReferralClick,
}: IAdjustersReferralListProps) => {
    const [filter, setFilter] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [referralToDelete, setReferralToDelete] = useState<number>(null);
    const [localIsStatusFilterActive, setLocalIsStatusFilterActive] = useState<boolean>(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [referralToCancel, setReferralToCancel] = useState<number>(null);

    const referralStatusOptions = {};
    referralStatus.forEach((referralStatus) => {
        const { referralStatusId, referralStatusLabel } = referralStatus;
        referralStatusOptions[referralStatusId] = referralStatusLabel;
    });

    const [localReferrals, setLocalReferrals] = useState<Array<ReferralEntity>>();
    const { InputFilters, isFilterActive } = useDateFilter(
        referrals,
        localReferrals,
        setLocalReferrals,
        'dateSubmitted',
        localIsStatusFilterActive,
    );
    const { InputFilters: StatusInputFilters, isFilterActive: isStatusFilterActive } = useDateFilter(
        referrals,
        localReferrals,
        setLocalReferrals,
        'referralStatusDate',
        isFilterActive,
    );

    useEffect(() => {
        setLocalIsStatusFilterActive(isStatusFilterActive);
    }, [isStatusFilterActive]);

    const handleConfirmCancelReferral = () => {
        console.log('handleConfirmCancelReferral');
        handleCancelReferral(referralToCancel, cancelReason ? 'Cancelled by Adjuster - ' + cancelReason : 'Cancelled By Adjuster');
        setShowCancelDialog(false);
        setCancelReason('');
    };

    const tableProps: MaterialTableProps<ReferralEntity> = {
        columns: [
            {
                title: 'Referral Number',
                render: (rowData) => {
                    return rowData.referralStatusId === ReferralStatusTypesEnum.Open ||
                        rowData.referralStatusId === ReferralStatusTypesEnum.SubmissionFailed ||
                        rowData.referralStatusId === ReferralStatusTypesEnum.ValidationFailed ||
                        rowData.referralStatusId === ReferralStatusTypesEnum.ReadyForSubmission ? (
                        <Link to={`/claims/${rowData.claimId}/referrals/edit/${rowData.referralId}`}>
                            {rowData.referralId}
                        </Link>
                    ) : (
                        <Link to={`/claims/${rowData.claimId}/referrals/${rowData.referralId}`}>
                            {rowData.referralId}
                        </Link>
                    );
                },
                customSort: (a, b) => a.referralId - b.referralId,
                field: 'referralId',
            },
            {
                title: 'Client',
                field: 'referralClientName',
            },
            { title: 'Vendor', field: 'vendorName' },
            {
                title: 'Date Submitted',
                field: 'dateSubmitted',
                render: (rowData) => (rowData.dateSubmitted ? moment(rowData.dateSubmitted).format('MM/DD/YYYY') : ''),
                customSort: (a, b) => new Date(a.dateSubmitted)?.getTime() - new Date(b.dateSubmitted)?.getTime(),
                filterComponent: () => InputFilters,
            },
            {
                title: 'Referral Status',
                field: 'referralStatusId',
                lookup: referralStatusOptions,
            },
            {
                title: 'Referral Status Date',
                field: 'referralStatusDate',
                render: (rowData) =>
                    rowData.referralStatusDate ? moment(rowData.referralStatusDate).format('M/D/YYYY') : '',
                customSort: (a, b) =>
                    new Date(a.referralStatusDate)?.getTime() - new Date(b.referralStatusDate)?.getTime(),
                filterComponent: () => StatusInputFilters,
            },
            { title: 'Service', field: 'serviceName' },
            {
                title: 'Service Type',
                field: 'joinedServiceTypeNames',
            },
        ],
        data: localReferrals ?? [],
        actions: [
            {
                icon: 'tune',
                tooltip: 'Filter',
                isFreeAction: true,
                onClick: () => setFilter(!filter),
            },
            {
                icon: (p) => (
                    <Button
                        {...p}
                        variant="contained"
                        color="primary"
                        style={{ marginLeft: 10, backgroundColor: '#107A98' }}
                    >
                        New Referral
                    </Button>
                ),
                isFreeAction: true,
                onClick: () => handleNewReferralClick(),
            },
            (rowData) => ({
                icon: (rowData) => (rowData.disabled ? <></> : <DeleteIcon style={{ fill: 'rgba(0,0,0,0.54)' }} />),
                tooltip: rowData.referralStatusId !== ReferralStatusTypesEnum.Open ? '' : 'Delete referral',
                position: 'row',
                onClick: (event, rowData) => {
                    if (Array.isArray(rowData)) return;
                    if (rowData.referralStatusId !== ReferralStatusTypesEnum.Open) return;
                    setReferralToDelete(rowData.referralId);
                    setShowConfirmDelete(true);
                },
                disabled: rowData.referralStatusId !== ReferralStatusTypesEnum.Open,
            }),
            (rowData) => ({
                icon: (rowData) => <CancelIconComponent rowData={rowData} />, 
                tooltip: 'Cancel referral',
                position: 'row',
                onClick: (event, rowData) => {
                    if (Array.isArray(rowData)) return;
                    setReferralToCancel(rowData.referralId);
                    setShowCancelDialog(true);
                },
                hidden: (
                    rowData.referralStatusId !== ReferralStatusTypesEnum.ValidationFailed
                ),
            }),
        ],
        options: {
            filtering: filter,
            showTitle: true,
            paging: referrals ? referrals.length >= 10 : false,
            toolbar: true,
            exportButton: true,
            search: true,
            exportAllData: true,
            pageSize:10,
            exportFileName: 'TerraClaimConnect_Referral_List',
            rowStyle: (rowData) => ({
                backgroundColor:
                    rowData.referralStatusId === ReferralStatusTypesEnum.SubmissionFailed ||
                    rowData.referralStatusId === ReferralStatusTypesEnum.ValidationFailed ||
                    rowData.referralStatusId === ReferralStatusTypesEnum.ReadyForSubmission
                        ? '#ff000042'
                        : 'inherit',
            }),
            actionsColumnIndex: -1,
        },
        localization: {
            header: {
                actions: '',
            },
        },
        title: 'Referrals',
    };

    return (
        <>
            <AdjustersReferralListTable tableProps={tableProps} />
            <Confirm
                onAction={() => {
                    handleDeleteReferral(referralToDelete);
                    setShowConfirmDelete(false);
                }}
                title="Delete Referral"
                messageText={`Are you sure you want to delete referral ${referralToDelete}?`}
                onToggleConfirm={() => {
                    setShowConfirmDelete(false);
                }}
                openDialog={showConfirmDelete}
                okText="Delete"
                noText="Cancel"
            />
            <Dialog open={showCancelDialog} onClose={() => setShowCancelDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle style={{ color: '#666' }}>
                    Cancel Referral
                    <IconButton
                        aria-label="close"
                        onClick={() => setShowCancelDialog(false)}
                        style={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText style={{ fontSize: '0.875rem' }}>
                    You are about to cancel the referral ({referralToCancel})
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Cancellation Reason"
                        type="text"
                        fullWidth
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        inputProps={{ maxLength: 2500 }}
                    />
                    <DialogContentText style={{ marginTop: 10 ,fontSize: '0.875rem'}} >
                    Are you sure you want to continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCancelDialog(false)}  className={styles().cancelButton}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmCancelReferral} className={styles().button}>
                        Yes, Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
